import {parseGid} from '@shopify/admin-graphql-api-utilities';
import {useI18n} from '@shopify/react-i18n';
import {useLocation, useNavigate} from 'react-router-dom';
import {useShop} from '@web-utilities/shop';
import {TranslatableResourceType} from 'types';
import {useLocalStorage} from '@web-utilities/local-storage';
import {THEME_ID_STORAGE_KEY} from 'features/Translations/constants';
import {useUrlParams} from 'utilities/use-url-params';
import {getSearchFromQuery} from 'utilities/url';

import {GROUPED_TRANSLATABLE_ROUTES} from './routes';

export const useGroupedTranslatableRoutes = () => {
  const [i18n] = useI18n();
  const location = useLocation();
  const {shopId, currentThemeId} = useShop();
  const navigate = useNavigate();
  const {urlParams} = useUrlParams();
  const currentPathname = location.pathname.replace('/localize', '');
  const [{value: storedThemeId}] =
    useLocalStorage<string>(THEME_ID_STORAGE_KEY);

  return GROUPED_TRANSLATABLE_ROUTES.map(({group, routes}) => ({
    label: i18n.translate(`useGroupedTranslatableRoutes.${group}`),
    items: routes
      .map((item) => {
        const regex = new RegExp(item.match ?? item.url, 'g');
        const current = regex.test(currentPathname);

        return {
          ...item,
          label: i18n.translate(`useGroupedTranslatableRoutes.${item.title}`),
          current,
          navigate: (push = true) => {
            const resourceType = item.resourceType;
            let url = item.url;

            const lowerCaseResourceType = resourceType.toLowerCase();
            const query: {[key: string]: unknown; item?: string} = {
              ...urlParams,
              item: undefined,
              filter: undefined,
            };
            if (
              [
                TranslatableResourceType.OnlineStoreThemeLocaleContent,
                TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
              ].includes(resourceType)
            ) {
              url = `/localize${item.url}`;
              query.id = storedThemeId
                ? storedThemeId
                : parseGid(currentThemeId);
            } else if (resourceType === TranslatableResourceType.Shop) {
              url = `/localize/${lowerCaseResourceType}`;
              query.id = shopId;
            } else if (resourceType === TranslatableResourceType.CookieBanner) {
              query.id = undefined;
              query.item = 'cookie_banner';
              url = `/localize${item.url}`;
            } else {
              query.id = undefined;
              url = `/localize${item.url}`;
            }

            navigate(
              {
                pathname: url,
                search: getSearchFromQuery(query),
              },
              {
                replace: !push,
              },
            );
          },
        };
      })
      .sort(({title: titleA}, {title: titleB}) => titleA.localeCompare(titleB)),
  }));
};
